"use client";

import LotImagePreview from "./LotImagePreview";
import { AuctionState, AuctionType, BidType, Lot, Role } from "@prisma/client";
import { Flex } from "@radix-ui/themes";
import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import LotPreviewInfo from "./LotPreviewInfo";
import Link from "next/link";
import { LotLiveData } from "@/app/api/auctions/[auctionId]/gridData/route";
import { useSearchParams } from "next/navigation";
import { WinningStatus, WinStatus } from "@/lib/enum/winStatus";
import { Button } from "@/app/components/ui/button";
import BidPopup from "@/app/components/BidPopup";
import { set } from "react-hook-form";
import axios, { AxiosError } from "axios";
import { useSession } from "next-auth/react";
import { appendSearchParams, cn, debugTimestamp, formatCurrency } from "@/lib/utils";
import useCustomToast from "@/lib/hooks/useCustomToast";
import { Router } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/app/components/ui/tooltip";
import { SavePaymentSelectionHandle } from "@/app/components/creditCardSelect/PaymentMethodSelect";
import CatalogueBidButton from "./CatalogueBidButton";
import { useAuctionTime } from "@/lib/context/AuctionTimeProvider";
import { SoftCloseContext } from "@/lib/context/SoftCloseContext";
import LotIdentifierInfo from "@/app/components/LotIdentifierInfo";
import LotValueInfo from "@/app/components/LotValueInfo";
import LeadingBar from "./LeadingBar";
import { Separator } from "@/app/components/ui/separator";
import { useSWRConfig } from "swr";
import { BidInfo } from "./lots/[lotId]/BiddingCard";
import { FLASH_DURATION } from "@/lib/constants";

interface Props {
  auctionId: string;
  lot: Lot;
  imageKey: string | undefined;
  polledData: LotLiveData;
  auctionStart: Date;
  auctionState: AuctionState;
  auctionType: AuctionType;
  loading: boolean;
  followers: number;
  following: boolean;
  winningStatus?: WinningStatus;
  showBidButton?: boolean;
  isFromWatchlist?: boolean;
}

const CLOSE_BUFFER = 3 * 1000;
// Maximum number of ms away from which the checkpoints (e.g., 3 seconds past close) can be to trigger the appropriate events
const TOLERANCE = 500;

const LotPreview = ({
  auctionId,
  lot,
  imageKey,
  polledData,
  winningStatus,
  auctionStart,
  auctionState,
  auctionType,
  loading,
  followers,
  following,
  showBidButton = true,
  isFromWatchlist = false,
}: Props) => {
  const session: any = useSession();
  const [isHovered, setIsHovered] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [animationClass, setAnimationClass] = useState("");
  const { mutate } = useSWRConfig();

  const [bidInfo, setBidInfo] = useState<BidInfo>({
    amount: polledData?.nextBid || 0,
    bidType: BidType.STANDARD,
  });

  const loadBidInfo = useCallback(() => {
    if (polledData?.nextBid) {
      setBidInfo({
        amount: polledData.nextBid,
        bidType: BidType.STANDARD,
      });
    }
  }, [polledData?.nextBid]);

  const suppressSelfUpdateRef = useRef(false);

  useEffect(() => {
    const nextAmount = polledData?.nextBid || 0;

    // If this update was triggered by the current user, consume suppression and skip all changes
    if (suppressSelfUpdateRef.current) {
      suppressSelfUpdateRef.current = false;
      return;
    }

    let shouldFlash = false;

    setBidInfo(prev => {
      shouldFlash = prev.amount !== nextAmount;
      if (shouldFlash) {
        return { ...prev, amount: nextAmount, flash: true };
      }
      if (prev.amount !== nextAmount || prev.flash) {
        return { ...prev, amount: nextAmount, flash: false };
      }
      return prev;
    });
  }, [polledData?.nextBid]);

  useEffect(() => {
    const unsetFlash = async () => {
      if (bidInfo.flash) {
        setTimeout(() => {
          setBidInfo(prev => ({ ...prev, flash: false }));
        }, FLASH_DURATION);
      }
    }

    unsetFlash();
  }, [bidInfo.flash, setBidInfo]);

  const searchParams = useSearchParams();
  const { toast, toastError } = useCustomToast();
  const savePaymentSelectionRef = useRef<SavePaymentSelectionHandle>(null);
  const isBuyer = session?.data?.role === Role.BUYER;

  const { currentTime } = useAuctionTime();
  const timeLeft = (new Date(polledData.endDate)).getTime() - (new Date(currentTime)).getTime();
  const softCloseContext = useContext(SoftCloseContext);
  const [hidden, setHidden] = useState(false);

  const confirmSimpleBid = useCallback(async () => {
    if (polledData?.nextBid && session) {
      try {
        suppressSelfUpdateRef.current = true;

        const response = await axios.post(`/api/auctions/${auctionId}/lots/${lot.id}/bids`, {
          session: session.data,
          amount: polledData?.nextBid,
          bidType: BidType.STANDARD,
        });
        setBidInfo({
          amount: polledData?.nextBid,
          bidType: BidType.STANDARD,
        });

        savePaymentSelectionRef.current?.savePaymentSelection();

        toast({
          title: "Success!",
          description: `You have placed a bid of ${formatCurrency(
            polledData.nextBid
          )}.`,
        });

        if (response?.data?.userId !== session?.data?.userId && response.data?.amount === polledData?.nextBid) {
          toast({
            title: "Auto-bid beat your bid!",
            description: "Your bid was placed, but someone set a max bid at the same amount or higher before you. To take the lead, please place a higher bid.",
            duration: 10000
          });
        } else if (response.data?.userId !== session?.data?.userId && response.data?.amount > polledData?.nextBid) {
          toast({
            title: "Outbid!",
            description: "You were outbid by someone who placed a max bid at least your amount. To lead, please place a higher bid."
          });
        }

        // Subscribe to the lot
        await fetch(`/api/account/subscriptions/subscribe`, {
          method: "POST",
          body: JSON.stringify({
            subscribe: true,
            lotId: lot.id,
            userId: session?.data?.userId,
            auctionId: parseInt(auctionId as string)
          })
        })

        mutate(`/api/account/subscriptions/${session.userId}/${lot.id}`, {
          isSubscribed: true
        })
      } catch (error) {
        if (error instanceof AxiosError) {
          console.error(JSON.stringify(error.response, null, 2));
          if (error?.response?.data?.error === "flagged") {
            toastError({
              title: "Bid Failed",
              description:
                "Your account has been suspended for suspicious activity. As a result, your bid has been rejected.",
            });
          } else {
            toastError({
              title: "Bidding Error",
              description: error?.response?.data?.message,
            });
          }
        } else {
          throw error;
        }
      } finally {
        mutate(`/api/auctions/${auctionId}/gridData`);
        mutate(`/api/auctions/${auctionId}/gridData/winning-status`);
      }
    }

    setIsOpen(false);
  }, [polledData, session, auctionId, lot.id, toast, toastError, mutate]);

  useEffect(() => {
    if (timeLeft <= -1000 && animationClass === ""
      && auctionState !== AuctionState.POST_AUCTION && !isFromWatchlist) {
      setAnimationClass("opacity-0 transition-opacity duration-1000");
    }
  }, [timeLeft, animationClass, auctionState]);

  const handleTransitionEnd = () => {
    // Make sure we're in the fade-out state, or you could add an extra check:
    if (animationClass && auctionState !== AuctionState.POST_AUCTION) {
      softCloseContext?.setNewCloseCount(softCloseContext?.newCloseCount + 1);
      setHidden(true);
    }
  };

  if (hidden && auctionState !== AuctionState.POST_AUCTION) {
    return null;
  }

  return (
    <>
      <Flex
        direction="column"
        className={cn("space-y-4 sm:space-y-4 relative", animationClass)}
        onTransitionEnd={handleTransitionEnd}
      >
        <BidPopup
          ref={savePaymentSelectionRef}
          isOpen={isOpen}
          setIsOpen={setIsOpen}
          auctionId={parseInt(auctionId)}
          onConfirm={async () => confirmSimpleBid()}
          onDismiss={() => setIsOpen(false)}
          bidInfo={bidInfo}
          flash={bidInfo.flash}
        />
        {/* Watchlist Mobile */}
        {isFromWatchlist && (
          <div className="md:!hidden">
            <Flex direction="column" gap="4">
              <Link
                href={`/auctions/${auctionId}/lots/${lot.id}`}
                prefetch={false}
                scroll={true}
              >
                <div>
                  <Flex justify={"between"} align="start">
                    <LotIdentifierInfo lot={lot} direction="row" />
                    <LeadingBar
                      winningStatus={winningStatus}
                      polledData={polledData}
                      className="static w-24"
                    />
                  </Flex>
                  <Flex justify="between">
                    <LotImagePreview
                      isBuyer={isBuyer}
                      loading={loading}
                      hover={isHovered}
                      imageKey={imageKey}
                      followers={followers}
                      following={following}
                      polledData={polledData}
                      auctionStart={auctionStart}
                      auctionState={auctionState}
                      auctionType={auctionType}
                      winningStatus={winningStatus}
                      compact={true}
                      lotId={lot.id}
                      auctionId={parseInt(auctionId)}
                      userId={session?.data?.userId || null}
                    />
                    <Flex
                      justify={"end"}
                      direction="column"
                      className="w-1/2 min-w-40"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                      }}
                    >
                      <LotValueInfo
                        lot={lot}
                        auctionState={auctionState}
                        polledData={polledData}
                      />
                      <CatalogueBidButton
                        auctionState={auctionState}
                        winningStatus={winningStatus}
                        polledData={polledData}
                        setIsOpen={(val: boolean) => {
                          loadBidInfo();
                          setIsOpen(val);
                        }}
                      />
                    </Flex>
                  </Flex>
                </div>
              </Link>
              <Separator />
            </Flex>
          </div>
        )}

        {/* Watchlist Desktop and Catalogue LotPreview*/}
        <div className={cn(isFromWatchlist && "!hidden md:!block")}>
          <Link
            href={appendSearchParams(`/auctions/${auctionId}/lots/${lot.id}`, searchParams)}
            prefetch={false}
            scroll={true}
          >
            <div
              style={{
                height: `33vw`,
                minHeight: `400px`,
                maxHeight: `450px`,
              }}
              onMouseEnter={() => setIsHovered(true)}
              onMouseLeave={() => setIsHovered(false)}
              className="mb-2 sm:mb-0 flex flex-col justify-start items-center gap-3"
            >
              <LotImagePreview
                isBuyer={isBuyer}
                loading={loading}
                hover={isHovered}
                followers={followers}
                following={following}
                imageKey={imageKey}
                polledData={polledData}
                auctionStart={auctionStart}
                auctionState={auctionState}
                auctionType={auctionType}
                winningStatus={winningStatus}
                lotId={lot.id}
                auctionId={parseInt(auctionId)}
                userId={session?.data?.userId || null}
              />
              <div className="w-full">
                <LotPreviewInfo
                  lot={lot}
                  auctionState={auctionState}
                  polledData={polledData}
                />
              </div>
            </div>
          </Link>
          {showBidButton && (
            <Flex mt="4">
              <CatalogueBidButton
                auctionState={auctionState}
                winningStatus={winningStatus}
                polledData={polledData}
                setIsOpen={(val: boolean) => {
                  loadBidInfo();
                  setIsOpen(val);
                }}
              />
            </Flex>
          )}
        </div>
      </Flex>
    </>
  );
};

export default LotPreview;
